import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name?.split(" ")[0] || "friend"}!`);
      const to =
        loc.state?.from ||
        (user.role === "super_admin" || user.role === "admin" || user.role === "staff" ? "/admin" : "/dashboard");
      navigate(to);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0A0A0A] text-white mb-4">
            <Lock className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Log in</h1>
          <p className="text-[#525252] mt-2">Access your CTP Kenya account</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white border border-[#0A0A0A] p-6 space-y-4">
          <GoogleButton label="Continue with Google" testId="google-signin-btn" />
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span>or with email</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Password</label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A]">
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 pr-12 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="login-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-[#525252] hover:text-[#0A0A0A]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <div className="text-sm text-[#DC2626] border border-[#DC2626] bg-[#DC2626]/5 px-3 py-2">{error}</div>}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#059669] text-white px-4 py-3 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Log in
          </button>
          <div className="text-center text-sm text-[#525252]">
            New here?{" "}
            <Link to="/signup" className="font-bold text-[#0A0A0A] hover:text-[#059669]">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
